import { useMemo } from 'react';
import { db } from '../db/client';
import type { Comment } from '../db/types';
import { avatarColor, avatarInitial, displayName } from './avatar';
import { relativeTime } from './relativeTime';

export function CommentsTab({ orgId }: { orgId: string }) {
  const { isLoading, data, error } = db.useQuery({
    comments: {
      $: { where: { 'organization.id': orgId }, order: { createdAt: 'desc' as const } },
      author: {},
    },
  });

  const groups = useMemo(() => groupByPage(data?.comments ?? []), [data?.comments]);
  const currentUrl = `${window.location.origin}${window.location.pathname}`;

  if (isLoading) return <div className="uin-muted">Loading…</div>;
  if (error) return <div className="uin-error">{error.message}</div>;

  if (groups.length === 0) {
    return (
      <p className="uin-muted">
        No comments yet. Click <b>+ Comment</b> on the toolbar, then click anywhere on the page.
      </p>
    );
  }

  return (
    <div>
      {groups.map((group) => (
        <div key={group.key} className="uin-comment-group">
          <header className="uin-comment-group-head">
            <span title={group.url}>{group.label}{group.url === currentUrl ? ' · here' : ''}</span>
            <span className="uin-muted">{group.comments.length}</span>
          </header>
          {group.comments.map((c) => {
            const color = avatarColor(c.authorEmail);
            return (
              <div
                key={c.id}
                className={'uin-comment-row' + (c.status === 'resolved' ? ' resolved' : '')}
                onClick={() => goTo(c)}
                role="button"
                tabIndex={0}
              >
                <div className="uin-comment-row-avatar" style={{ background: color.bg, color: color.fg }}>
                  {avatarInitial(c.authorEmail)}
                </div>
                <div className="uin-comment-row-content">
                  <div className="uin-comment-row-head">
                    <span className="uin-comment-row-name">{displayName(c.authorEmail)}</span>
                    <span className="uin-comment-row-time">{relativeTime(c.createdAt)}</span>
                  </div>
                  <div className="uin-comment-body">{c.body}</div>
                  {c.status === 'resolved' && (
                    <span className="uin-badge resolved" style={{ marginTop: 4 }}>resolved</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function groupByPage(comments: Comment[]) {
  const map = new Map<string, { key: string; label: string; url: string; comments: Comment[] }>();
  for (const c of comments) {
    const key = `${c.pageOrigin}${c.pagePath}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: shortenUrl(c.pageOrigin, c.pagePath),
        url: c.pageOrigin + c.pagePath,
        comments: [],
      });
    }
    map.get(key)!.comments.push(c);
  }
  return Array.from(map.values());
}

function shortenUrl(origin: string, path: string): string {
  return origin.replace(/^https?:\/\//, '') + (path === '/' ? '' : path);
}

function goTo(c: Comment) {
  const here = `${window.location.origin}${window.location.pathname}`;
  if (here === c.pageOrigin + c.pagePath) {
    // Already on this page; just dispatch a focus event so PinLayer scrolls.
    window.dispatchEvent(new CustomEvent('uinoter:focus-comment', { detail: c.id }));
    return;
  }
  const u = new URL(c.pageUrl);
  u.searchParams.set('uinoter_focus', c.id);
  window.open(u.toString(), '_blank');
}
